import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { metadataApi, type Solution, type Industry, type BusinessCategory } from '../api/metadataApi'
import { useToast } from '../../../shared/components/toast/useToast'

// ── Solutions ──────────────────────────────────────────────────────────────────

export const useSolutions = () =>
  useQuery<Solution[]>({
    queryKey: ['solutions'],
    queryFn: metadataApi.listSolutions,
    staleTime: 5 * 60 * 1000,
  })

export const useCreateSolution = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (data: { slug: string; name: string; description?: string }) =>
      metadataApi.createSolution(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solutions'] })
      toast.success('Solution created')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to create solution'),
  })
}

export const useUpdateSolution = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: Partial<{ name: string; description: string }> }) =>
      metadataApi.updateSolution(slug, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solutions'] })
      toast.success('Solution updated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to update solution'),
  })
}

export const useDeactivateSolution = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (slug: string) => metadataApi.deactivateSolution(slug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['solutions'] })
      toast.success('Solution deactivated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to deactivate solution'),
  })
}

// ── Industries ─────────────────────────────────────────────────────────────────

export const useIndustriesMeta = () =>
  useQuery<Industry[]>({
    queryKey: ['industries-meta'],
    queryFn: metadataApi.listIndustries,
    staleTime: 5 * 60 * 1000,
  })

export const useCreateIndustry = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (data: { name: string }) => metadataApi.createIndustry(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['industries-meta'] })
      qc.invalidateQueries({ queryKey: ['industries'] })
      toast.success('Industry created')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to create industry'),
  })
}

export const useUpdateIndustry = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string } }) =>
      metadataApi.updateIndustry(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['industries-meta'] })
      qc.invalidateQueries({ queryKey: ['industries'] })
      toast.success('Industry updated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to update industry'),
  })
}

export const useDeactivateIndustry = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (id: string) => metadataApi.deactivateIndustry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['industries-meta'] })
      qc.invalidateQueries({ queryKey: ['industries'] })
      toast.success('Industry deactivated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to deactivate industry'),
  })
}

// ── Business Categories ────────────────────────────────────────────────────────

export const useBusinessCategoriesMeta = () =>
  useQuery<BusinessCategory[]>({
    queryKey: ['business-categories-meta'],
    queryFn: metadataApi.listBusinessCategories,
    staleTime: 5 * 60 * 1000,
  })

export const useCreateBusinessCategory = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (data: { name: string }) => metadataApi.createBusinessCategory(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-categories-meta'] })
      qc.invalidateQueries({ queryKey: ['business-categories'] })
      toast.success('Business category created')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to create business category'),
  })
}

export const useUpdateBusinessCategory = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string } }) =>
      metadataApi.updateBusinessCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-categories-meta'] })
      qc.invalidateQueries({ queryKey: ['business-categories'] })
      toast.success('Business category updated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to update business category'),
  })
}

export const useDeactivateBusinessCategory = () => {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (id: string) => metadataApi.deactivateBusinessCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-categories-meta'] })
      qc.invalidateQueries({ queryKey: ['business-categories'] })
      toast.success('Business category deactivated')
    },
    onError: (err: unknown) => toast.error((err as Error)?.message ||'Failed to deactivate business category'),
  })
}
